import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import toast from 'react-hot-toast'

export default function Login() {
    const [mode, setMode] = useState('password') // 'password' or 'pin'
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [pin, setPin] = useState('')
    const [loading, setLoading] = useState(false)
    const [companyName, setCompanyName] = useState('Timesheet System')
    const { login, loginWithPin } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        async function loadCompany() {
            try {
                const res = await fetch('/api/company-settings/public')
                const data = await res.json()
                if (data.success && data.companyName) {
                    setCompanyName(data.companyName)
                    document.title = `${data.companyName} Timesheets`
                }
            } catch (error) {
                // Keep defaults
            }
        }
        loadCompany()
    }, [])

    const handlePasswordLogin = async (e) => {
        e.preventDefault()
        setLoading(true)

        const result = await login(username, password)

        if (result.success) {
            toast.success('Welcome back!')
            navigate('/')
        } else {
            toast.error(result.message)
        }

        setLoading(false)
    }

    const handlePinLogin = async () => {
        if (pin.length < 4) return

        setLoading(true)
        const result = await loginWithPin(pin)

        if (result.success) {
            toast.success('Welcome back!')
            navigate('/')
        } else {
            toast.error(result.message)
            setPin('')
        }

        setLoading(false)
    }

    const handlePinInput = (digit) => {
        if (pin.length < 6) {
            const newPin = pin + digit
            setPin(newPin)
            if (newPin.length >= 4) {
                // Auto-submit after 4 digits (can be configured)
            }
        }
    }

    const handlePinBackspace = () => {
        setPin(pin.slice(0, -1))
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-rdi-blue to-rdi-blue-light flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="bg-rdi-blue p-6 text-center">
                    <img
                        src="/api/company-settings/logo/left"
                        alt="Company Logo"
                        className="h-12 mx-auto mb-2"
                    />
                    <h1 className="text-white text-xl font-semibold">{companyName}</h1>
                </div>

                {/* Mode Toggle */}
                <div className="flex border-b">
                    <button
                        onClick={() => setMode('password')}
                        className={`flex-1 py-3 font-medium transition-colors ${mode === 'password'
                                ? 'text-rdi-blue border-b-2 border-rdi-blue'
                                : 'text-gray-500'
                            }`}
                    >
                        Password
                    </button>
                    <button
                        onClick={() => setMode('pin')}
                        className={`flex-1 py-3 font-medium transition-colors ${mode === 'pin'
                                ? 'text-rdi-blue border-b-2 border-rdi-blue'
                                : 'text-gray-500'
                            }`}
                    >
                        PIN
                    </button>
                </div>

                {/* Login Form */}
                <div className="p-6">
                    {mode === 'password' ? (
                        <form onSubmit={handlePasswordLogin}>
                            <Input
                                label="Username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                autoComplete="username"
                                required
                            />
                            <Input
                                label="Password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                required
                            />
                            <Button
                                type="submit"
                                block
                                loading={loading}
                                className="mt-4"
                            >
                                Sign In
                            </Button>
                        </form>
                    ) : (
                        <div>
                            {/* PIN Display */}
                            <div className="flex justify-center gap-3 mb-6">
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                    <div
                                        key={i}
                                        className={`w-4 h-4 rounded-full ${i < pin.length ? 'bg-rdi-blue' : 'bg-gray-200'
                                            }`}
                                    />
                                ))}
                            </div>

                            {/* PIN Pad */}
                            <div className="grid grid-cols-3 gap-3">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'back'].map((key, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        disabled={key === null || loading}
                                        onClick={() => {
                                            if (key === 'back') {
                                                handlePinBackspace()
                                            } else if (key !== null) {
                                                handlePinInput(key.toString())
                                            }
                                        }}
                                        className={`
                      py-4 text-2xl font-medium rounded-lg transition-colors
                      ${key === null ? 'invisible' : ''}
                      ${key === 'back'
                                                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                : 'bg-gray-100 hover:bg-gray-200 active:bg-gray-300'}
                    `}
                                    >
                                        {key === 'back' ? '⌫' : key}
                                    </button>
                                ))}
                            </div>

                            <Button
                                onClick={handlePinLogin}
                                block
                                loading={loading}
                                disabled={pin.length < 4}
                                className="mt-6"
                            >
                                Sign In with PIN
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}